// =============================================================================
// FILE: packages/renderer/src/i18n/catalogues.ts
// LAYER: B7 — translation catalogues per locale.
//
// Adding a new locale = adding a key map here. No code edits anywhere
// else (per success criterion #3 of P1-08).
//
// Missing keys fall back to the English string via the t() helper —
// never blank, never error.
// =============================================================================

export type LocaleCode = 'en' | 'fr' | 'es' | 'de' | 'fil';

type Catalogue = Record<string, string>;

export const CATALOGUES: Record<LocaleCode, Catalogue> = {
  en: {
    'nav.dashboard':      'Dashboard',
    'nav.cost':           'Cost Templates (B8)',
    'nav.backup':         'Backups (B9)',
    'nav.health':         'Health (P1.4)',
    'nav.settings':       'Settings',
    'settings.language.title':       'Language',
    'settings.language.description': 'Choose the language used across the UI. AI responses follow the same selection — runtime translation is best-effort when no live translator is configured.',
    'settings.language.label':       'Interface language',
    'settings.language.providerInfo':'Active translation provider:',
    'settings.language.applied':     'Preference saved. The interface re-rendered immediately.',
    'settings.language.noLive':      'No live translator configured — AI output may fall back to English.',
    'cost.title':         'Cost templates',
    'cost.subtitle':      'Company-specific estimating rate packs. Every edit forks a new immutable version.',
    'backup.title':       'Backups',
  },
  fr: {
    'nav.dashboard':      'Tableau de bord',
    'nav.cost':           'Modèles de coûts (B8)',
    'nav.backup':         'Sauvegardes (B9)',
    'nav.settings':       'Paramètres',
    'settings.language.title':       'Langue',
    'settings.language.description': 'Choisissez la langue utilisée dans toute l’interface. Les réponses IA suivent la même sélection — la traduction est au mieux lorsqu’aucun traducteur en direct n’est configuré.',
    'settings.language.label':       'Langue de l’interface',
    'settings.language.providerInfo':'Fournisseur de traduction actif :',
    'settings.language.applied':     'Préférence enregistrée. L’interface a été mise à jour immédiatement.',
    'settings.language.noLive':      'Aucun traducteur actif configuré — la sortie IA peut revenir à l’anglais.',
    'cost.title':         'Modèles de coûts',
    'cost.subtitle':      'Tarifs d’estimation spécifiques à l’entreprise. Chaque modification crée une nouvelle version immuable.',
    'backup.title':       'Sauvegardes',
  },
  es: {
    'nav.dashboard':      'Tablero',
    'nav.cost':           'Plantillas de costos (B8)',
    'nav.backup':         'Copias de seguridad (B9)',
    'nav.settings':       'Configuración',
    'settings.language.title':       'Idioma',
    'settings.language.description': 'Elija el idioma utilizado en toda la interfaz. Las respuestas de IA siguen la misma selección — la traducción es mejor esfuerzo cuando no hay un traductor en vivo configurado.',
    'settings.language.label':       'Idioma de la interfaz',
    'settings.language.providerInfo':'Proveedor de traducción activo:',
    'settings.language.applied':     'Preferencia guardada. La interfaz se actualizó al instante.',
    'settings.language.noLive':      'No hay traductor activo — la salida de IA puede caer al inglés.',
    'cost.title':         'Plantillas de costos',
    'cost.subtitle':      'Paquetes de tarifas de estimación específicos de la empresa. Cada edición bifurca una nueva versión inmutable.',
    'backup.title':       'Copias de seguridad',
  },
  de: {
    'nav.dashboard':      'Übersicht',
    'nav.cost':           'Kostenvorlagen (B8)',
    'nav.backup':         'Sicherungen (B9)',
    'nav.settings':       'Einstellungen',
    'settings.language.title':       'Sprache',
    'settings.language.description': 'Wählen Sie die in der Oberfläche verwendete Sprache. KI-Antworten folgen derselben Auswahl — die Laufzeitübersetzung erfolgt nach bestem Bemühen, wenn kein aktiver Übersetzer konfiguriert ist.',
    'settings.language.label':       'Oberflächensprache',
    'settings.language.providerInfo':'Aktiver Übersetzungsanbieter:',
    'settings.language.applied':     'Einstellung gespeichert. Die Oberfläche wurde sofort neu gerendert.',
    'settings.language.noLive':      'Kein aktiver Übersetzer konfiguriert — KI-Ausgabe kann auf Englisch zurückfallen.',
    'cost.title':         'Kostenvorlagen',
    'cost.subtitle':      'Firmenspezifische Schätzungstarife. Jede Bearbeitung erstellt eine neue, unveränderliche Version.',
    'backup.title':       'Sicherungen',
  },
  fil: {
    'nav.dashboard':      'Dashboard',
    'nav.cost':           'Mga Cost Template (B8)',
    'nav.backup':         'Mga Backup (B9)',
    'nav.settings':       'Mga Setting',
    'settings.language.title':       'Wika',
    'settings.language.description': 'Piliin ang wikang ginagamit sa interface. Susunod ang mga sagot ng AI sa parehong pinili — best-effort ang pagsasalin kapag walang aktibong tagasalin.',
    'settings.language.label':       'Wika ng interface',
    'settings.language.providerInfo':'Aktibong tagapagsalin:',
    'settings.language.applied':     'Naitago ang preference. Agad na nag-update ang interface.',
    'settings.language.noLive':      'Walang aktibong tagasalin — maaaring bumalik sa Ingles ang AI output.',
    'cost.title':         'Mga Cost Template',
    'cost.subtitle':      'Mga rate pack ng pagtatantya na partikular sa kompanya. Ang bawat edit ay gumagawa ng bagong immutable na bersyon.',
    'backup.title':       'Mga Backup',
  },
};

export const SUPPORTED_LOCALES_RENDERER: ReadonlyArray<{
  code: LocaleCode; nativeName: string; displayName: string;
}> = [
  { code: 'en',  nativeName: 'English',  displayName: 'English' },
  { code: 'fr',  nativeName: 'Français', displayName: 'French' },
  { code: 'es',  nativeName: 'Español',  displayName: 'Spanish' },
  { code: 'de',  nativeName: 'Deutsch',  displayName: 'German' },
  { code: 'fil', nativeName: 'Filipino', displayName: 'Filipino' },
];
